import 'knockout.validation';
import ko from 'knockout';
import axios from 'axios';
import {
    Status,
    CreateMeetingRequest,
    CreateMeetingResponse,
} from './interfaces';
import { Log } from './log';
import { DateTimeViewModel } from './date-time-view-model';
import { EmailViewModel } from './email-view-model';
import MicroModal from 'micromodal';
import { initDatePicker, initTimePicker } from './form-helpers';

export class CreateMeetingViewModel {
    private _validator: KnockoutValidationGroup = ko.validatedObservable(this);

    public errors = ko.observableArray([]);
    public warning = ko.observable('');
    public success = ko.observable(false);
    public isSubmitting = ko.observable(false);
    public startDateTime = new DateTimeViewModel();
    public link = ko.observable<string>();
    public copied = ko.observable<boolean>(false);
    public validateNow = ko.observable(false);
    public participantEmails = ko.observableArray<EmailViewModel>([
        new EmailViewModel(this),
    ]);

    public validParticipantEmails = ko.pureComputed(() => {
        return this.participantEmails().filter((p) => p.isValid());
    });
    departmentId: number;

    constructor(departmentId: number) {
        this.participantEmails.subscribe<KnockoutArrayChange<EmailViewModel>[]>(
            (changes) => {
                changes.forEach((change) => {
                    if (change.status === 'added') {
                        // subscribe to changes to new elements
                        change.value.value.subscribe(this.emailChanged);
                    }
                });
            },
            null,
            'arrayChange',
        );

        this.departmentId = departmentId;
        this._validator = ko.validatedObservable(this);
        this._validator.errors.showAllMessages(false);
    }

    public disableValidation = (): void => {
        if (this.validateNow()) {
            this.validateNow(false);
        }
    };

    public reset = (): void => {
        this.success(false);
        this.errors([]);
        this.participantEmails([new EmailViewModel(this)]);
        initDatePicker('#startDate', (selectedDates) => {
            this.startDateTime.date(selectedDates[0]);
        });
        initTimePicker('#startTime', (selectedDates) => {
            this.startDateTime.time(selectedDates[0]);
        });
        this.validateNow(false);
        this._validator.errors.showAllMessages(false);
        this.startDateTime.setCurrentDateAndTime();
    };

    private emailChanged = (): void => {
        const lastEmail =
            this.participantEmails()[this.participantEmails().length - 1];
        const email = lastEmail.value();
        if (email && email.length > 0) {
            this.participantEmails.push(new EmailViewModel(this));
        }
    };

    public createMeeting = (): void => {
        const numberOfNonEmptyFields = this.participantEmails().filter(
            (x) => x.value() && x.value().length > 0,
        ).length;
        const someEmailsAddressesAreNotValid =
            this.validParticipantEmails().length < numberOfNonEmptyFields;

        if (someEmailsAddressesAreNotValid) {
            this.validateNow(true);
            return;
        }

        if (this.isSubmitting()) {
            return;
        }

        this.isSubmitting(true);

        const request: CreateMeetingRequest = {
            participantEmails: this.validParticipantEmails().map((p) =>
                p.value(),
            ),
            departmentId: this.departmentId,
            startTime: this.startDateTime.dateTime(),
            isPublic: true,
        };

        axios
            .post<Status<CreateMeetingResponse>>('/api/meetings', request)
            .then((response) => {
                const data = response.data;
                if (data.hasErrors) {
                    this.errors(data.errors.map((e) => e.value));
                    return;
                }

                const failedEmailReceipients =
                    response.data.result.failedEmailReceipients;
                const hasFailedEmailReceipients =
                    failedEmailReceipients.length > 0;
                if (hasFailedEmailReceipients) {
                    const participiantsNotNotified =
                        this.validParticipantEmails().filter((x) =>
                            failedEmailReceipients.includes(x.value()),
                        );
                    if (participiantsNotNotified) {
                        this.participantEmails.removeAll(
                            participiantsNotNotified,
                        );
                    }

                    const commaSeperatedListOfUnnotifiedReceipients =
                        failedEmailReceipients.reduce((prev, curr) => {
                            return prev + '<li> ' + curr + '</li>';
                        });

                    this.warning(
                        `Invitationen kunne ikke sendes til: <ul> ${commaSeperatedListOfUnnotifiedReceipients} </ul>Du kan kopiere linket til mødet og invitere dem via en almindelig e-mail i stedet.`,
                    );
                }

                this.link(response.data.result.videoRoomLink);
                this.success(true);
            })
            .catch((error) => {
                if (error.response) {
                    // The request was made and the server responded with a status code
                    // that falls out of the range of 2xx
                    Log.error(
                        'Error creating meeting. ' +
                            error.response.status +
                            ': ' +
                            error.response.data,
                    );
                } else if (error.request) {
                    // The request was made but no response was received
                    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                    // http.ClientRequest in node.js
                    Log.error(
                        'Error creating meeting. No response from server',
                        error.request,
                    );
                } else {
                    // Something happened in setting up the request that triggered an Error
                    Log.error('Error creating meeting', error.message);
                }
                this.errors(['Kunne ikke oprette videosamtalen. Prøv igen.']);
            })
            .finally(() => {
                this.isSubmitting(false);
            });
    };

    public copyLink = (): void => {
        const input = document.getElementById(
            'created-meeting-link-input',
        ) as HTMLInputElement;
        input.select();

        try {
            this.copied(document.execCommand('copy'));
        } catch {
            this.copied(false);
        }
    };

    public showModal = (): void => {
        this.reset();
        MicroModal.show('create-meeting-modal', {
            awaitCloseAnimation: true,
            disableFocus: true,
        });
    };
}

export function CreateMeeting(departmentId: number): void {
    ko.applyBindings(
        new CreateMeetingViewModel(departmentId),
        document.getElementById('create-meeting-link'),
    );
}
